import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@agms/ui'
import { applicationApi, documentApi, type Application } from '../../../lib/api'
import { apiErrorMessage } from '../../../hooks/useErrorHandler'
import { FileUploadZone } from '../FileUploadZone'
import { SectionBody, SectionHeader } from './StageContextLayout'

export function ClientReturnedContext({ app }: { app?: Application | null }) {
  const queryClient = useQueryClient()
  const [responseNote, setResponseNote] = useState('')
  const [uploadUrl, setUploadUrl] = useState<string | null>(null)
  const [uploadUrlCopied, setUploadUrlCopied] = useState(false)
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{ id: string; file_name: string }>>([])
  const [hasManagedUploadSelection, setHasManagedUploadSelection] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const generateUploadLink = useMutation({
    mutationFn: async () => {
      if (!app) throw new Error('No application')
      return applicationApi.generateFiservReturnUploadLink(app.id)
    },
    onSuccess: (data) => {
      setUploadUrl(data.upload_url)
      setActionMessage(`Upload link generated. Expires ${data.expires_at ? new Date(data.expires_at).toLocaleString() : 'soon'}.`)
      setActionError(null)
    },
    onError: (error) => {
      setActionMessage(null)
      setActionError(apiErrorMessage(error, 'Could not generate upload link.'))
    },
  })

  const resubmit = useMutation({
    mutationFn: async () => {
      if (!app) throw new Error('No application')
      return applicationApi.resubmitToFiserv(app.id, {
        note: responseNote.trim(),
        ...(hasManagedUploadSelection ? { document_ids: uploadedDocuments.map((document) => document.id) } : {}),
      })
    },
    onSuccess: () => {
      setResponseNote('')
      setActionError(null)
      setActionMessage('Resubmitted to Fiserv. This lead is moving back to Underwriting Review.')
      if (app?.lead_id) {
        queryClient.invalidateQueries({ queryKey: ['lead', app.lead_id] })
      }
    },
    onError: (error) => {
      setActionMessage(null)
      setActionError(apiErrorMessage(error, 'Fiserv resubmission failed.'))
    },
  })

  const uploadSupportingDocument = useMutation({
    mutationFn: async (file: File) => {
      if (!app?.lead_id) throw new Error('No lead')
      return documentApi.upload(app.lead_id, {
        file,
        label: 'Fiserv Return Supporting Document',
        category: 'supporting_docs',
        is_restricted: false,
      })
    },
    onSuccess: (document, file) => {
      setHasManagedUploadSelection(true)
      setUploadedDocuments((current) => [
        ...current,
        { id: document.id, file_name: document.file_name || file.name },
      ])
      setActionError(null)
      setActionMessage(`${document.file_name || file.name} uploaded. You can resubmit once the response note is ready.`)
      if (app?.lead_id) {
        queryClient.invalidateQueries({ queryKey: ['lead-documents', app.lead_id] })
      }
    },
    onError: (error) => {
      setActionMessage(null)
      setActionError(apiErrorMessage(error, 'Document upload failed.'))
    },
  })

  const removeUploadedDocument = (documentId: string) => {
    setHasManagedUploadSelection(true)
    setUploadedDocuments((current) => current.filter((document) => document.id !== documentId))
    setActionMessage('Removed from this resubmission. The file remains in Document Center for audit history.')
    setActionError(null)
  }

  const copyUploadUrl = async () => {
    if (!uploadUrl) return
    await navigator.clipboard.writeText(uploadUrl)
    setUploadUrlCopied(true)
    window.setTimeout(() => setUploadUrlCopied(false), 1400)
  }

  return (
    <>
      <SectionHeader color="#fee2e2">
        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#991b1b' }}>
          Client Returned — Action Required
        </h3>
      </SectionHeader>
      <SectionBody>
        {app?.fiserv_credit_officer_comments ? (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            marginBottom: '12px',
          }}>
            <p style={{ fontSize: '12px', fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
              Credit Officer Comments:
            </p>
            <p style={{ fontSize: '14px', color: '#7f1d1d', lineHeight: 1.5 }}>
              {app.fiserv_credit_officer_comments}
            </p>
          </div>
        ) : (
          <p style={{ fontSize: '14px', color: '#991b1b', marginBottom: '12px' }}>
            Fiserv has returned this application. Check the credit officer comments for required actions.
          </p>
        )}
        <p style={{ fontSize: '13px', color: '#6b7280' }}>
          Upload supporting documents here, use Document Center below, or generate a merchant upload link if the merchant needs to send files.
        </p>

        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#fff',
          border: '1px solid #fecaca',
          borderRadius: '6px',
        }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Upload supporting document
          </p>
          <FileUploadZone
            onFileSelect={(file) => uploadSupportingDocument.mutate(file)}
            accept="application/pdf,image/jpeg,image/png,image/heic,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            maxSizeMb={25}
            isUploading={uploadSupportingDocument.isPending}
            disabled={!app?.lead_id}
            compact
          />
          {uploadedDocuments.length > 0 && (
            <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
              {uploadedDocuments.map((document) => (
                <div
                  key={document.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '6px 8px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#065f46',
                    fontWeight: 500,
                  }}
                >
                  <span>Attached for resubmission: {document.file_name}</span>
                  <button
                    type="button"
                    onClick={() => removeUploadedDocument(document.id)}
                    aria-label={`Remove ${document.file_name} from this resubmission`}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#166534',
                      cursor: 'pointer',
                      fontSize: '16px',
                      lineHeight: 1,
                      padding: '0 2px',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            onClick={() => generateUploadLink.mutate()}
            isLoading={generateUploadLink.isPending}
            disabled={!app?.id || generateUploadLink.isPending}
          >
            Generate Upload Link
          </Button>
          {uploadUrl && (
            <Button variant="outline" onClick={copyUploadUrl}>
              {uploadUrlCopied ? 'Copied' : 'Copy Upload Link'}
            </Button>
          )}
        </div>

        {uploadUrl && (
          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            backgroundColor: '#fff',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            wordBreak: 'break-all',
            fontSize: '12px',
            color: '#374151',
          }}>
            {uploadUrl}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            resubmit.mutate()
          }}
          style={{ marginTop: '16px' }}
        >
          <label htmlFor="fiserv-response-note" style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
            AGMS response note for Fiserv
          </label>
          <textarea
            id="fiserv-response-note"
            value={responseNote}
            onChange={(e) => setResponseNote(e.currentTarget.value)}
            placeholder="Summarize what was corrected and which supporting documents are attached."
            aria-describedby="fiserv-response-note-help"
            style={{
              width: '100%',
              minHeight: '96px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '14px',
              resize: 'vertical',
            }}
          />
          <p id="fiserv-response-note-help" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            At least one uploaded supporting document is required before resubmitting.
          </p>
          <div style={{ marginTop: '10px' }}>
            <Button
              type="submit"
              isLoading={resubmit.isPending}
              disabled={!app?.id || responseNote.trim().length < 10 || resubmit.isPending}
            >
              Resubmit to Fiserv
            </Button>
          </div>
        </form>

        {actionMessage && (
          <p style={{ fontSize: '13px', color: '#065f46', marginTop: '10px', fontWeight: 500 }}>
            {actionMessage}
          </p>
        )}
        {actionError && (
          <p style={{ fontSize: '13px', color: '#b91c1c', marginTop: '10px', fontWeight: 500 }}>
            {actionError}
          </p>
        )}
      </SectionBody>
    </>
  )
}
